import axios, { AxiosRequestHeaders } from 'axios';

const baseURL = process.env.REACT_APP_API_URL || '/api/v1/eshop';

export const eshopApi = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 20000,
});

let cachedSessionToken: string | null = null;

export function getOrCreateEshopSessionToken(): string {
  if (cachedSessionToken) {
    return cachedSessionToken;
  }

  // In non-browser environments (tests, SSR) generate an in-memory token
  if (typeof window === 'undefined') {
    cachedSessionToken = `eshop-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
    return cachedSessionToken;
  }

  const key = 'eshop_session_token';
  const existing = window.localStorage.getItem(key);
  let token: string;
  if (existing) {
    token = existing;
  } else {
    if (window.crypto && typeof (window.crypto as any).randomUUID === 'function') {
      token = (window.crypto as any).randomUUID();
    } else {
      token = `eshop-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
    }
    window.localStorage.setItem(key, token);
  }

  // Persist token in a cookie as well so the backend can read it when headers are missing
  try {
    const oneYearInSeconds = 60 * 60 * 24 * 365;
    document.cookie = `eshop_session_token=${token}; path=/; max-age=${oneYearInSeconds}`;
  } catch {
    // Ignore cookie errors (e.g. disabled cookies)
  }

  cachedSessionToken = token;
  return token;
}

eshopApi.interceptors.request.use((config) => {
  const token = getOrCreateEshopSessionToken();
  const headers: AxiosRequestHeaders = (config.headers || {}) as AxiosRequestHeaders;
  if (!('X-Session-Token' in headers)) {
    (headers as any)['X-Session-Token'] = token;
  }
  config.headers = headers;
  return config;
});

export interface EshopProductVariant {
  id: number;
  sku?: string;
  name?: string;
  stock_qty?: number;
  image_url?: string;
}

export interface EshopProduct {
  id: number;
  slug: string;
  name: string;
  short_description?: string;
  description_html?: string;
  price_cents: number;
  currency: string;
  default_image_url?: string;
  gallery_json?: string;
  tags?: string;
  variants?: EshopProductVariant[];
}

export interface EshopCartItem {
  id: number;
  product_id: number;
  variant_id?: number;
  quantity: number;
  unit_price_cents: number;
  currency: string;
  product?: EshopProduct;
}

export interface EshopCart {
  id: number;
  currency: string;
  items: EshopCartItem[];
}

export async function getProducts(): Promise<EshopProduct[]> {
  const res = await eshopApi.get<{ data: EshopProduct[] }>('/products');
  return res.data.data;
}

export async function getProduct(slug: string): Promise<EshopProduct> {
  const res = await eshopApi.get<EshopProduct>(`/products/${encodeURIComponent(slug)}`);
  return res.data;
}

export async function getCart(): Promise<EshopCart> {
  const res = await eshopApi.get<EshopCart>('/cart');
  return res.data;
}

export async function addToCart(productId: number, variantId: number | undefined, quantity: number): Promise<void> {
  await eshopApi.post('/cart/items', {
    product_id: productId,
    variant_id: variantId ?? undefined,
    quantity,
  });
}

export async function updateCartItem(id: number, quantity: number): Promise<void> {
  await eshopApi.patch(`/cart/items/${id}`, { quantity });
}

export const removeCartItem = async (id: number): Promise<void> => {
  await eshopApi.delete(`/cart/items/${id}`);
};

export interface CheckoutRequest {
  billing_address: any;
  shipping_address: any;
  shipping_method: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
}

export interface CheckoutResponse {
  order_id: number;
  order_number: string;
  payment_provider?: string;
  payment_redirect_url?: string;
  manual_payment?: boolean;
  contact_email?: string;
}

export async function checkout(req: CheckoutRequest): Promise<CheckoutResponse> {
  const res = await eshopApi.post<CheckoutResponse>('/checkout', req);
  return res.data;
}

export const getPacketaWidgetConfig = async (): Promise<{ api_key: string; env: string }> => {
  const response = await eshopApi.get('/shipping/packeta-widget-config');
  return response.data;
};

export interface EshopOrderItem {
  id: number;
  name: string;
  quantity: number;
  unit_price_cents: number;
  currency: string;
}

export interface EshopOrder {
  id: number;
  order_number: string;
  status: string;
  total_amount_cents: number;
  currency: string;
  items?: EshopOrderItem[];
}

export async function getOrder(id: number): Promise<EshopOrder> {
  const res = await eshopApi.get<EshopOrder>(`/orders/${id}`);
  return res.data;
}

// --- Setup & Settings ---

export interface EshopSettings {
  id?: number;
  default_currency: string;
  supported_currencies?: string;
  default_country: string;
  shipping_options_json?: string;
  terms_url?: string;
  returns_policy_url?: string;
  support_email?: string;
  support_phone?: string;
}

export interface ClubInfo {
  club_name: string;
  club_logo_url: string;
  contact_email: string;
  contact_phone: string;
  contact_country: string;
  primary_color: string;
}

export async function getEshopSettings(): Promise<EshopSettings> {
  const res = await eshopApi.get<EshopSettings>('/admin/settings');
  return res.data;
}

export async function updateEshopSettings(settings: EshopSettings): Promise<EshopSettings> {
  const res = await eshopApi.put<EshopSettings>('/admin/settings', settings);
  return res.data;
}

export async function getClubInfo(): Promise<ClubInfo> {
  const res = await eshopApi.get<ClubInfo>('/admin/club-info');
  return res.data;
}
